import React, {
  FC,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import styles from "./styles.module.less";
import { LaunchpadLayout } from "../../components/LaunchpadLayout/index";
import axios from "axios";
import {
  Empty,
  message,
  Spin,
  Modal,
  Button,
  Menu,
  Dropdown,
  Tooltip,
} from "antd";
import {
  isIp,
  isHost,
  isLarkBrowser,
  isIPhonOrIPad,
  openWindow,
} from "../../utils/index";
import { useTitle } from "../../hooks/useTitle";
import { useTranslation, Trans } from "react-i18next";
import { Protocol } from "@/config";
import { ReactSortable } from "react-sortablejs";
import classnames from "classnames";
import { AppTypeTag } from "@/components/AppTypeTag";
import { IconFont } from "@/components/IconFont";
import { useNavigate } from "react-router";
import qs from "query-string";

// import HtmlWebpackPlugin from "html-webpack-plugin";

// 工具函数
const repleaceSecondLevelDomain = (target: string, source: string) => {
  if (!isIp(target) && isHost(target)) {
    const arr = target.split(".");
    arr[0] = source;
    return arr.join(".");
  }
  return target;
};

const APP_ITEM_WIDTH = 180;
const APP_ITEM_MARGIN = 12;

const InModalApps = (props: any) => {
  const { t } = useTranslation();

  const { allApps, isInstalled, installAppInModel, removeApp } = props;
  return (
    <div className={styles.store}>
      {allApps.map((v: any) => (
        <div key={v.id} className={styles.item}>
          <img className={styles.logo} src={v.logo} alt="" />
          <div className={styles.appNameAndDesc}>
            <div className={styles.appNameContainer}>
              <h3 title={v.name} className={styles.appName}>
                {v.name}
              </h3>
              <AppTypeTag type={v.isIntegrate ? "integrate" : "selfBuilt"} />
            </div>
            <Tooltip title={v.description}>
              <p title={v.description}>
                {v.description || t("common.noDescNow")}
              </p>
            </Tooltip>
          </div>
          {isInstalled(v.id) ? (
            <Button type="default" size="small" onClick={() => removeApp(v)}>
              {t("common.uninstall")}
            </Button>
          ) : (
            <Button
              type="primary"
              size="small"
              onClick={() => installAppInModel(v.id)}
            >
              {t("common.install")}
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};

export const LaunchpadPage: FC = () => {
  const { t } = useTranslation();
  useTitle(`${window.__config__.name} ${t("common.appPanel")}`, "");

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState("100%");

  const computedContainerWidth = useCallback(
    () =>
      requestAnimationFrame(() => {
        if (!containerRef.current) {
          return;
        }

        const containerWidth = containerRef.current.clientWidth;

        // 我也看不懂
        const count = parseInt(
          String(
            (containerWidth + APP_ITEM_MARGIN * 2) /
              (APP_ITEM_WIDTH + APP_ITEM_MARGIN * 2)
          )
        );

        setContainerWidth(
          count * APP_ITEM_WIDTH + APP_ITEM_MARGIN * (count * 2) + "px"
        );
      }),
    []
  );

  useEffect(() => {
    computedContainerWidth();

    window.addEventListener("resize", computedContainerWidth);

    return () => window.removeEventListener("resize", computedContainerWidth);
  }, [computedContainerWidth]);

  const [orderedApps, setOrderedApps] = useState<any[]>([]);

  const [allApps, setAllApps] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [loadingAllApp, setLoadingAllApp] = useState(true);
  const [addVisible, setAddVisible] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState("");
  const [, setUninstallApps] = useState<string[]>([]);
  const navigate = useNavigate();

  // 集成应用无权限登录跳转过来
  useEffect(() => {
    const search = window.location.search;
    const query = qs.parse(search);

    if (Number(query.deny_login)) {
      message.error(
        t("common.sorryForNoPermissionLogin", {
          name: query.deny_app_name,
        })
      );
    }
    delete query.deny_login;
    delete query.deny_app_name;

    navigate(`/launchpad?${qs.stringify(query)}`);
  }, [navigate, t]);

  // 所有 app，包括未安装的
  const fetchAllApps = useCallback(async () => {
    setLoadingAllApp(true);
    try {
      const res = await axios.get<{
        list: any[];
        totalCount: number;
      }>(`/api/v2/users/launchpad/all-applications`);

      setAllApps(res.data.list);
    } finally {
      setLoadingAllApp(false);
    }
  }, []);

  // 获取用户已经安装的 apps
  const fetchOrderedApps = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get<{
        list: any[];
        totalCount: number;
        config: {
          uninstallApps: string[];
        };
      }>(`/api/v2/users/launchpad/applications`);

      setOrderedApps(res.data.list);
      setUninstallApps(res.data.config.uninstallApps);
    } finally {
      setLoading(false);
    }
  }, []);

  // 初始化
  useEffect(() => {
    if (!window.__user__) {
      window.location.href = "/login?logout=1";
      return;
    }
    fetchOrderedApps();
    fetchAllApps();
  }, [fetchAllApps, fetchOrderedApps]);

  const isInstalled = useCallback(
    (id) => {
      return orderedApps.some((item) => item.id.split("/")[0] === id);
    },
    [orderedApps]
  );

  const removeAppRequest = useCallback(
    async (id: string) => {
      setLoading(true);
      try {
        const res: any = await axios.delete(
          `/api/v2/users/launchpad/apps/?ids=${id}`
        );
        if (res.code !== 200) {
          message.error(res.message);
        }
      } finally {
        fetchOrderedApps();
        setLoading(false);
      }
    },
    [fetchOrderedApps]
  );

  const removeApp = useCallback(
    async (v: any) => {
      if (v.id.includes("/")) {
        // 子应用删除
        return Modal.confirm({
          title: t("common.deleteApp"),
          width: 600,
          content: (
            <Trans
              i18nKey="user.launchpadDeleteApp"
              values={{
                mainAppName: v.mainAppName,
                subAppName: v.name,
              }}
            >
              删除<b> {v.name} </b>
              应用的同时会删除其他所有
              <b> {v.mainAppName} </b>
              应用，你确定要删除此应用吗？注：只要你有访问权限，你可以随时通过按钮「添加应用」把它们添回到你的应用面板。
            </Trans>
          ),
          onOk() {
            return removeAppRequest(v.id.split("/")[0]);
          },
        });
      }

      removeAppRequest(v.id);
    },
    [removeAppRequest, t]
  );

  // 添加应用，只在 modal 里使用
  const installAppInModel = useCallback(
    async (addId: string) => {
      setLoading(true);
      let ids = orderedApps.map((i) => i.id);
      if (addId) {
        ids = ids.concat(addId);
      }
      try {
        let res: any = await axios.post(`/api/v2/users/launchpad/order`, {
          ids: ids,
        });
        if (res.code !== 200) {
          message.error(res.message);
        }
      } finally {
        fetchOrderedApps();
        setLoading(false);
      }
    },
    [fetchOrderedApps, orderedApps]
  );

  // 调整应用顺序
  const sortApps = useCallback(async () => {
    setLoading(true);
    let ids = orderedApps.map((i) => i.id);
    try {
      let res: any = await axios.post(`/api/v2/users/launchpad/order`, {
        ids: ids,
      });
      if (res.code !== 200) {
        message.error(res.message);
      }
    } finally {
      fetchOrderedApps();
      setLoading(false);
    }
  }, [fetchOrderedApps, orderedApps]);

  const appListElm = useMemo(() => {
    let containerClick = (v: any) => {
      let url: string;
      let query: Record<string, any> = {};
      // 集成应用的登录
      if (v.isIntegrate) {
        url = `/apps/${v.id}`;
      } else if (v.protocol === Protocol.SAML) {
        url =
          `${window.location.protocol}//` +
          `${repleaceSecondLevelDomain(window.location.host, v.identifier)}` +
          `/api/v2/saml-idp/${v.id}`;
      } else {
        // 跳转到此应用的域名下
        url =
          `${window.location.protocol}//` +
          `${repleaceSecondLevelDomain(window.location.host, v.identifier)}` +
          `/login`;
        query.app_id = v.id;
      }

      const appUrl = `${url}?${qs.stringify(query)}`;

      if (isLarkBrowser()) {
        axios
          .post("/cas/create-temporary-token")
          .then(({ data: token }) => {
            const createTokenQuery = {
              token,
              redirect_uri: appUrl,
            };
            openWindow(
              `/cas/login-by-temporary-token?${qs.stringify(createTokenQuery)}`
            );
          })
          .catch(() => openWindow(appUrl));
      } else {
        openWindow(appUrl);
      }
    };

    return orderedApps?.map((v) => {
      const menu = (
        <Menu>
          <Menu.Item>
            <div className={styles.editTipFloat} onClick={() => removeApp(v)}>
              <IconFont type="authing-delete-bin-6-line" />
              <span className={styles.deleteText}>{t("common.deleteApp")}</span>
            </div>
          </Menu.Item>
        </Menu>
      );
      return (
        <div
          key={v.id}
          className={classnames(styles.app)}
          onClick={() => {
            // iphon 和 ipad 上无法点击
            if (!isIPhonOrIPad()) {
              containerClick(v);
            }
          }}
          onTouchEnd={() => {
            if (isIPhonOrIPad()) {
              containerClick(v);
            }
          }}
          onMouseLeave={() => setDropdownVisible("")}
        >
          <div
            id={v.id}
            onClick={(evt) => {
              evt.stopPropagation();
            }}
            className={classnames(styles.point3)}
          >
            <Dropdown
              overlay={menu}
              getPopupContainer={() =>
                document.getElementById(`${v.id}`) || document.body
              }
              placement="bottomCenter"
              trigger={["click"]}
              visible={dropdownVisible === v.id}
              onVisibleChange={(visible) =>
                setDropdownVisible(visible ? v.id : "")
              }
            >
              <IconFont type="authing-more-fill" className={styles.point} />
            </Dropdown>
            {/* <IconFont type="authing-more-fill" /> */}
          </div>
          <div className={styles.appLogoWrap}>
            <img
              src={v.logo}
              alt=""
              draggable={false}
              className={styles.logo}
            />
          </div>
          <p className={styles.appName}>{v.name}</p>
        </div>
      );
    });
  }, [dropdownVisible, orderedApps, removeApp, t]);

  const AddButton = () => {
    return (
      <div
        className={classnames(styles.app, styles.addLogo, styles.addBtn)}
        onClick={() => setAddVisible(true)}
      >
        <div className={classnames(styles.logo, styles.appLogoWrap)}>+</div>
        <p className={styles.appName}>{t("common.addApp")}</p>
      </div>
    );
  };

  return (
    <LaunchpadLayout>
      <div
        className={styles.container}
        onDragEnd={() => {
          sortApps();
        }}
      >
        <Spin spinning={loading}>
          <div
            ref={containerRef}
            style={{
              display: "flex",
              justifyContent: "center",
              minHeight: "80vh",
            }}
          >
            <div
              style={{
                width: containerWidth,
              }}
              className={styles.appList}
            >
              {!loadingAllApp && Boolean(allApps?.length) && <AddButton />}

              {!loadingAllApp && allApps?.length <= 0 && (
                <div className={styles.emptyApps}>
                  <Empty description={t("common.noOpenedApp")} />
                </div>
              )}

              {orderedApps.length > 0 && (
                // 很蠢，ReactSortable 的 disabled 无法响应
                <ReactSortable
                  className={styles.sortTable}
                  filter={`.${styles.addAppWrap}`}
                  list={orderedApps}
                  setList={(list) => {
                    setOrderedApps(list);
                  }}
                >
                  {appListElm}
                </ReactSortable>
              )}
            </div>
          </div>
        </Spin>
      </div>

      <Modal
        title={t("common.installApp")}
        visible={addVisible}
        width={806}
        bodyStyle={{
          minHeight: 300,
          maxHeight: "78vh",
          overflow: "auto",
        }}
        footer={false}
        onCancel={() => setAddVisible(false)}
      >
        {allApps.length ? (
          <InModalApps
            allApps={allApps}
            isInstalled={isInstalled}
            installAppInModel={installAppInModel}
            removeApp={removeApp}
          />
        ) : (
          <div
            style={{
              textAlign: "center",
            }}
          >
            <Empty />
          </div>
        )}
      </Modal>
    </LaunchpadLayout>
  );
};
